/**
 * NP-505 — DBA branch accent theming.
 *
 * A DBA scheme is one attribute on <html>: `data-theme="<id>"`, whose palette is
 * the matching `[data-theme="<id>"]` block in `src/index.css`. It composes with —
 * rather than replaces — the `.dark` class that `lib/theme.ts` toggles, so the
 * light/dark control keeps working untouched (AC4) and no DBA context at all
 * leaves the PATH scheme in force (AC5).
 */
import { DBA_THEMES, type DbaTheme } from '@/config/dbaThemes';
import { loEmailFromSearch, normaliseLoEmail, readCapturedLoEmail } from '@/lib/loEmail';

export const DBA_THEME_ATTRIBUTE = 'data-theme';

/**
 * NP-505 QA: the officer each loan resolved to last time, so switching back to a
 * loan re-themes on the first paint instead of after a network round-trip. The
 * officer is cached rather than the theme id because it survives a routing-table
 * change, and because a loan whose officer maps to NO theme must be remembered as
 * "default" rather than falling through to whatever was painted before.
 */
export const LO_BY_LOAN_STORAGE_KEY = 'path-dba-lo-by-loan';

type LoanOfficerByLoan = Record<string, string>;

function readLoanOfficerCache(): LoanOfficerByLoan {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(LO_BY_LOAN_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Values are filtered rather than cast: this is read during render, and
    // `normaliseLoEmail` would throw on a non-string (optional chaining guards
    // null/undefined, not a number) — crashing the tree over a hand-edited or
    // future-shaped localStorage entry.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
    );
  } catch {
    return {};
  }
}

/** The officer this loan resolved to on a previous visit, if any. */
export function recalledLoanOfficerEmail(loanId: string | null | undefined): string | null {
  if (!loanId) return null;
  return normaliseLoEmail(readLoanOfficerCache()[loanId]);
}

export function rememberLoanOfficerEmail(loanId: string, email: string): void {
  try {
    if (typeof window === 'undefined') return;
    const normalised = normaliseLoEmail(email);
    if (!normalised) return;
    const cache = readLoanOfficerCache();
    if (cache[loanId] === normalised) return;
    cache[loanId] = normalised;
    window.localStorage.setItem(LO_BY_LOAN_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Theming is cosmetic — a full or disabled localStorage must never throw here.
  }
}

export function resolveDbaThemeId(
  loanOfficerEmail: string | null | undefined,
  themes: readonly DbaTheme[] = DBA_THEMES,
): string | null {
  const email = normaliseLoEmail(loanOfficerEmail);
  if (!email) return null;
  const domain = email.slice(email.indexOf('@') + 1);
  return themes.find(
    (t) => t.loanOfficerEmails.includes(email) || t.loanOfficerDomains?.includes(domain),
  )?.id ?? null;
}

export function applyDbaTheme(themeId: string | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (themeId) {
    root.setAttribute(DBA_THEME_ATTRIBUTE, themeId);
  } else {
    root.removeAttribute(DBA_THEME_ATTRIBUTE);
  }
}

/**
 * The officer whose branch should colour the app before any loan has loaded:
 * this navigation's `?lo=`, else the last one captured from an earlier one.
 * Reading the URL directly (rather than waiting for LOEmailCapture to persist it)
 * is what lets a first-ever visit to a `?lo=` link paint in the DBA's colours.
 */
export function entryLoanOfficerEmail(search?: string): string | null {
  if (typeof window === 'undefined') return null;
  // NP-547: through `returnUrl` too, so a wrapped link themes login/registration on
  // first paint (NP-505 AC2) instead of only after the post-login redirect.
  return loEmailFromSearch(search ?? window.location.search) ?? readCapturedLoEmail();
}

/** Resolve and apply in one call, for the pre-render pass in main.tsx. */
export function syncDbaThemeFromEntry(): void {
  applyDbaTheme(resolveDbaThemeId(entryLoanOfficerEmail()));
}
